import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDate } from '../../../lib/formatDate';
import { errorMessage, toastError, toastSuccess } from '../../../lib/toast';
import {
  adminAddOwner, adminGetTag, adminLinkDepartment, adminListDepartments, adminRemoveOwner,
  adminRevokeGrant, adminUnlinkDepartment, listOf,
} from '../../vendors/vendorsApi';
import { LEVELS, LEVEL_LABEL, VENDOR_ERRORS } from '../../vendors/vendorLabels';
import { Dialog, Drawer, InlineError, Skeleton } from '../../vendors/VendorBits';
import { CollaboratorPicker } from '../../buildApp/AppRequestForm';
import '../../vendors/Vendors.css';

// Active-user search for the named-owner picker (vendors.manage; `{users:[...]}`).
const OWNER_SEARCH_URL = '/api/admin/vendors/users/search';
const SOURCE_LABEL = { request: 'Request', manual: 'Manual' };

const idOf = (o) => o.user_id ?? o.id;

// What each level adds on top of the one before it (server/routes/vendorActions.js
// gates: comment = comment, propose = upload + proposals, approve = review + acknowledge
// + deciding proposals). Managers of an Approve-linked department are owners (access.js).
export const LEVEL_HELP = {
  view: 'Can see the vendors in this speciality.',
  comment: 'Can also comment on them.',
  propose: 'Can also upload documents and propose changes.',
  approve: "Can also approve changes and record reviews. The department's managers are listed as owners.",
};

function vendorsPhrase(count) {
  if (typeof count !== 'number') return 'the vendors';
  return count === 1 ? 'the 1 vendor' : `the ${count} vendors`;
}

function Section({ title, lede, meta, label, children }) {
  return (
    <section className="vd-sd-section" aria-label={label || title}>
      <div className="vd-sd-head">
        <h3 className="vd-sd-title">{title}</h3>
        {meta && <span className="vd-panel-meta">{meta}</span>}
      </div>
      {lede && <p className="vd-sd-lede">{lede}</p>}
      {children}
    </section>
  );
}

/** One speciality's access: department links, named owners, personal grants. */
export default function SpecialityDrawer({ tag, onClose }) {
  const [state, setState] = useState({ status: 'loading', data: null });
  const [departments, setDepartments] = useState({ status: 'loading', items: [] });
  const [pickDept, setPickDept] = useState('');
  const [pickLevel, setPickLevel] = useState('view');
  const [revoking, setRevoking] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminGetTag(tag.id)
      .then((d) => setState({ status: 'ready', data: d?.detail || d }))
      .catch(() => setState((s) => (s.data ? s : { status: 'error', data: null })));
  }, [tag.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let live = true;
    adminListDepartments()
      .then((d) => { if (live) setDepartments({ status: 'ready', items: listOf(d, 'departments') }); })
      // A failed read is said out loud, never shown as "no departments to link".
      .catch(() => { if (live) setDepartments({ status: 'error', items: [] }); });
    return () => { live = false; };
  }, []);

  // Runs a write, toasts the outcome, then re-reads so the lists show server truth.
  const mutate = async (fn, okMessage, failMessage) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      if (okMessage) toastSuccess(okMessage);
      load();
    } catch (err) {
      toastError(errorMessage(err, failMessage, VENDOR_ERRORS));
    } finally {
      setBusy(false);
    }
  };

  const data = state.data || {};
  const linked = useMemo(() => state.data?.departments || [], [state.data]);
  const owners = data.owners || [];
  const managerOwners = data.manager_owners || [];
  const grants = data.grants || [];
  const linkedIds = useMemo(() => new Set(linked.map((d) => d.department_id ?? d.id)), [linked]);
  const free = departments.items.filter((d) => !linkedIds.has(d.id));

  const link = () => {
    if (!pickDept) return;
    mutate(() => adminLinkDepartment(tag.id, Number(pickDept), pickLevel), 'Department linked.', 'Could not link the department.');
    setPickDept('');
  };

  let deptPlaceholder = 'Select a department';
  if (departments.status === 'loading') deptPlaceholder = 'Loading departments…';
  else if (departments.status === 'ready' && free.length === 0) deptPlaceholder = 'Every department is linked';

  let body;
  if (state.status === 'loading') body = <Skeleton rows={6} label="Loading speciality" />;
  else if (state.status === 'error') body = <InlineError message="Couldn't load this speciality. Try again in a minute." />;
  else {
    body = (
      <>
        <Section
          title="Departments"
          meta={linked.length ? `${linked.length} linked` : null}
          lede={`Everyone in a linked department, and its managers, gets that level on every vendor in ${tag.name}. Each level includes the ones before it.`}
        >
          {linked.length === 0 ? (
            <p className="vd-sd-empty">No departments linked yet.</p>
          ) : (
            <ul className="vd-sd-list">
              {linked.map((d) => {
                const deptId = d.department_id ?? d.id;
                const name = d.name || d.department_name;
                return (
                  <li key={deptId} className="vd-sd-row">
                    <strong className="vd-sd-name">{name}</strong>
                    <select
                      className="vd-select vd-sd-level"
                      aria-label={`Level for ${name}`}
                      value={d.level}
                      disabled={busy}
                      onChange={(e) => mutate(() => adminLinkDepartment(tag.id, deptId, e.target.value), 'Level updated.', 'Could not change the level.')}
                    >
                      {LEVELS.map((l) => <option key={l} value={l}>{LEVEL_LABEL[l]}</option>)}
                    </select>
                    <button
                      type="button"
                      className="vd-text-btn vd-text-btn--danger"
                      disabled={busy}
                      aria-label={`Remove ${name}`}
                      onClick={() => mutate(() => adminUnlinkDepartment(tag.id, deptId), 'Department unlinked.', 'Could not unlink the department.')}
                    >
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="vd-sd-add">
            <div className="vd-sd-field">
              <label htmlFor="vd-link-dept">Department</label>
              <select
                id="vd-link-dept"
                className="vd-select"
                value={pickDept}
                onChange={(e) => setPickDept(e.target.value)}
                disabled={busy || departments.status !== 'ready' || free.length === 0}
              >
                <option value="">{deptPlaceholder}</option>
                {free.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="vd-sd-field">
              <label htmlFor="vd-link-level">Level</label>
              <select
                id="vd-link-level"
                className="vd-select"
                value={pickLevel}
                onChange={(e) => setPickLevel(e.target.value)}
                disabled={busy}
                aria-describedby="vd-link-level-help"
              >
                {LEVELS.map((l) => <option key={l} value={l}>{LEVEL_LABEL[l]}</option>)}
              </select>
            </div>
            <button type="button" className="btn btn-primary vd-btn-compact vd-sd-add-btn" onClick={link} disabled={busy || !pickDept}>Link department</button>
            <p id="vd-link-level-help" className="vd-sd-help" aria-live="polite">
              <strong>{LEVEL_LABEL[pickLevel]}.</strong> {LEVEL_HELP[pickLevel]}
            </p>
          </div>
          {departments.status === 'error' && (
            <InlineError message="Couldn't load the department list, so none can be linked right now." />
          )}
        </Section>

        <Section title="Owners" meta={owners.length ? `${owners.length} named` : null}>
          <div className="vd-sd-picker">
            <CollaboratorPicker
              collaborators={owners.map((o) => ({ id: idOf(o), name: o.name, email: o.email, photo_url: o.photo_url }))}
              onAdd={(u) => mutate(() => adminAddOwner(tag.id, u.id), 'Owner added.', 'Could not add the owner.')}
              onRemove={(id) => mutate(() => adminRemoveOwner(tag.id, id), 'Owner removed.', 'Could not remove the owner.')}
              searchUrl={OWNER_SEARCH_URL}
              hint="Named owners can approve every change in this speciality."
              placeholder="Search people by name or email…"
              noMatchLabel="No people match"
            />
          </div>
          {managerOwners.length > 0 && (
            <section className="vd-sd-sub" aria-label="Owners through department management">
              <h4 className="vd-sd-subtitle">Owners through department management</h4>
              <ul className="vd-sd-list">
                {managerOwners.map((m) => (
                  <li key={`${idOf(m)}-${m.department_name || ''}`} className="vd-sd-row vd-sd-row--static">
                    <span className="vd-sd-name">{m.name}</span>
                    {m.department_name && <span className="vd-list-meta">Manages {m.department_name}</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </Section>

        <Section
          title="People"
          meta={grants.length ? `${grants.length} ${grants.length === 1 ? 'person' : 'people'}` : null}
          lede="Personal access given to one person, outside their department."
        >
          {grants.length === 0 ? (
            <p className="vd-sd-empty">Personal access comes from approved access requests. None yet.</p>
          ) : (
            <ul className="vd-sd-list">
              {grants.map((g) => (
                <li key={idOf(g)} className="vd-sd-row">
                  <div className="vd-sd-name">
                    <strong>{g.name}</strong>
                    <div className="vd-list-meta">
                      {SOURCE_LABEL[g.source] || g.source}
                      {g.granted_by_name ? ` · granted by ${g.granted_by_name}` : ''}
                      {g.granted_at ? ` · ${formatDate(g.granted_at)}` : ''}
                    </div>
                  </div>
                  <span className="vd-chip vd-sd-chip">{LEVEL_LABEL[g.level] || g.level}</span>
                  <button
                    type="button"
                    className="vd-text-btn vd-text-btn--danger"
                    disabled={busy}
                    aria-label={`Revoke ${g.name}`}
                    onClick={() => setRevoking(g)}
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </>
    );
  }

  return (
    <>
      <Drawer
        open
        wide
        title={tag.name}
        subtitle={`Who can see and change ${vendorsPhrase(tag.vendor_count)} in ${tag.name}.`}
        onClose={onClose}
      >
        <div className="vd-sd">{body}</div>
      </Drawer>
      {revoking && (
        <Dialog title={`Revoke ${revoking.name}'s access?`} onClose={() => setRevoking(null)} labelledBy="vd-revoke-title">
          <p>{revoking.name} will lose their personal {LEVEL_LABEL[revoking.level] || revoking.level} access to {tag.name}.</p>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setRevoking(null)}>Cancel</button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => { const g = revoking; setRevoking(null); mutate(() => adminRevokeGrant(tag.id, idOf(g)), 'Access revoked.', 'Could not revoke the access.'); }}
            >
              Revoke access
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}
